import { FunctionComponent, ReactNode, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

import { classNames } from "@/utils/classNames";

interface TooltipProps {
	children: ReactNode;
	content: ReactNode | string;
	disabled?: boolean;
	className?: string;
	delay?: number;
	direction?: string;
	exitDelay?: number;
	isOpen?: boolean;
	/** "dark" matches the PREC-3249 status/lock tooltip styling. Defaults to the light card. */
	variant?: "light" | "dark";
	/**
	 * Widens the tooltip for rich panel content (e.g. the Lead Scoring / segment
	 * hovers, which are ~320px cards). Without this the default `max-w-[310px]`
	 * clips the card's right edge (prio badge, overall-score value).
	 */
	panel?: boolean;
}

export const Tooltip: FunctionComponent<TooltipProps> = ({
	children,
	content,
	disabled = false,
	className,
	delay = 200,
	direction = "top",
	exitDelay = 150,
	isOpen = false,
	variant = "light",
	panel = false,
}) => {
	const timerRef = useRef<NodeJS.Timeout | null>(null);
	const [isVisible, setIsVisible] = useState(isOpen);
	const [isVisibleDelayed, setIsVisibleDelayed] = useState(false);

	useEffect(() => {
		if (disabled) {
			setIsVisible(false);
		}
	}, [disabled]);

	useEffect(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}

		if (isVisible) {
			timerRef.current = setTimeout(() => {
				setIsVisibleDelayed(isVisible);
			}, delay);
		} else {
			timerRef.current = setTimeout(() => {
				setIsVisibleDelayed(isVisible);
			}, exitDelay);
		}

		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
	}, [isVisible, delay, exitDelay]);

	const tooltipRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLDivElement>(null);

	// Resolve the portal target from the trigger's root node so the tooltip renders in
	// the same DOM subtree as its styles.
	// - Web app: `getRootNode()` is the document → `document.body` (unchanged).
	// - Extension: the panel mounts inside a shadow root whose app lives in an inner
	//   `<body>` (WXT / @webext-core/isolated-element builds an inner HTML document).
	//   The shadow-scoped Tailwind stylesheet and its base font apply inside that inner
	//   document, so portal there. Portaling to the raw `ShadowRoot` puts the tooltip
	//   outside the inner document, where it inherits only the `:host { all: initial }`
	//   reset and renders unstyled / in the UA serif font.
	// Resolved inline (not via state) so the correct target is used on the first hover:
	// an effect-deferred target renders once into the wrong place and only settles on a
	// later hover.
	const resolvePortalTarget = (): HTMLElement | ShadowRoot => {
		const root = triggerRef.current?.getRootNode();

		if (root instanceof ShadowRoot) {
			return root.querySelector("body") ?? root;
		}

		return document.body;
	};

	useEffect(() => {
		if (isVisibleDelayed && triggerRef.current && tooltipRef.current) {
			const triggerRect = triggerRef.current.getBoundingClientRect();
			const tooltipRect = tooltipRef.current.getBoundingClientRect();
			const gap = 8;
			const padding = 16;

			// Coordinates are viewport-relative (the tooltip uses `position: fixed`), so
			// `getBoundingClientRect()` is used as-is without adding `window.scrollX/Y`.
			// This keeps positioning correct regardless of where the portal lives in the
			// DOM (document body or a shadow root).
			let top = 0;
			let left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

			if (direction === "top") {
				top = triggerRect.top - tooltipRect.height - gap;
				if (triggerRect.top - tooltipRect.height - gap < 0) {
					top = triggerRect.bottom + gap;
				}
			} else if (direction === "bottom") {
				top = triggerRect.bottom + gap;
				if (triggerRect.bottom + tooltipRect.height + gap > window.innerHeight) {
					top = triggerRect.top - tooltipRect.height - gap;
				}
			} else if (direction === "top-right") {
				left = triggerRect.right + gap;

				if (triggerRect.bottom + tooltipRect.height + gap < window.innerHeight) {
					top = triggerRect.bottom + gap;
				} else {
					top = triggerRect.top - tooltipRect.height - gap;
				}
			}

			if (left < padding) {
				left = padding;
			} else if (left + tooltipRect.width > window.innerWidth - padding) {
				left = window.innerWidth - tooltipRect.width - padding;
			}

			tooltipRef.current.style.left = `${left}px`;

			tooltipRef.current.style.top = `${top}px`;
		}
	}, [isVisibleDelayed, direction]);

	return (
		<>
			<div
				ref={triggerRef}
				className={classNames(className, "group inline-flex relative z-100 min-w-0")}
				onMouseEnter={() => {
					!disabled && setIsVisible(true);
				}}
				onMouseLeave={() => !disabled && setIsVisible(false)}
				onFocus={() => !disabled && setIsVisible(true)}
				onBlur={() => !disabled && setIsVisible(false)}
			>
				{children}
				{isVisibleDelayed &&
					ReactDOM.createPortal(
						<div
							ref={tooltipRef}
							// Keep the tooltip open while the cursor is over it so its content can be
							// scrolled / clicked. The trigger's onMouseLeave starts the hide timer when
							// the cursor crosses the gap; entering the tooltip cancels it.
							onMouseEnter={() => !disabled && setIsVisible(true)}
							onMouseLeave={() => !disabled && setIsVisible(false)}
							className={classNames(
								"fixed p-2 rounded shadow-lg z-[21474836399] transition-opacity text-sm max-h-[420px] overflow-hidden",
								panel ? "max-w-[360px]" : "max-w-[310px]",
								variant === "dark"
									? "bg-gray-900 border border-gray-700 text-white"
									: "bg-white border border-gray-200"
							)}
						>
							<div className="w-full pb-4 max-h-[420px] overflow-x-hidden overflow-y-auto scrollbar-hide h-full w-full">
								{content}
							</div>
						</div>,
						resolvePortalTarget()
					)}
			</div>
		</>
	);
};
